import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Drawer, IconButton, Box, List, ListItem,
  ListItemIcon, ListItemText, useTheme, useMediaQuery, Avatar
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Business, Logout
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 260;

const adminMenuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: Dashboard, path: '/admin/dashboard' },
  { id: 'schools', label: 'Schools', icon: Business, path: '/admin/schools' },
];

const AdminLayout = ({ children, admin, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
    } else {
      setSidebarOpen((prev) => !prev);
    }
  };

  const drawerContent = (
    <div>
      <Toolbar sx={{ minHeight: '56px !important', display: 'flex', alignItems: 'center', gap: 1 }}>
        <img src="/Logo.png" alt="School Sync" style={{ height: '32px', width: 'auto' }} />
        <Typography variant="h6" noWrap color="primary" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
          Platform Admin
        </Typography>
      </Toolbar>
      <List>
        {adminMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <ListItem
              key={item.id}
              component="div"
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                py: 0.75,
                cursor: 'pointer',
                position: 'relative',
                '&::before': {
                  display: isActive ? 'block' : 'none',
                  content: '""',
                  position: 'absolute',
                  left: 0, top: 0, bottom: 0, width: '3px',
                  bgcolor: 'primary.main',
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                  '& .MuiListItemIcon-root, & .MuiTypography-root': { color: 'primary.main' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: isActive ? 'primary.main' : 'text.secondary' }}>
                <Icon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.82rem',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'primary.main' : 'text.primary',
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { xs: 0, md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontSize: '0.95rem' }}>
            School Sync — Central Administration
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="inherit" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {admin?.name || admin?.email}
            </Typography>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark', fontSize: '0.85rem' }}>
              {(admin?.name || admin?.email || '?')[0].toUpperCase()}
            </Avatar>
            <IconButton color="inherit" onClick={onLogout} title="Logout">
              <Logout fontSize="small" />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { xs: 0, md: sidebarOpen ? drawerWidth : 0 },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { xs: '100%', md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          mt: { xs: 7, md: 8 },
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
